<script setup>
// ========================================
// "Replace special characters" checkbox and the "123 z 160 zn. | 2 SMS × 0,11 zł" counter shown under SmsTextarea.
// Wider than xs both are columns of the parent's flex row (next to its own buttons); xs: checkbox above the counter.
// ========================================
import { useDisplay } from 'vuetify'
import { useCalendarStore } from '@/views/apps/calendar/useCalendarStore'
import { formatNumber } from '@/utils/strings'
import { getSmsInfo, SMS_MAX_PARTS } from '@/utils/sms'

const props = defineProps({
  text: {
    type: String,
    default: '',
  },

  // Recipient's phone - decides between the domestic and the international price
  phone: {
    type: String,
    default: '',
  },
  shortLabel: {
    type: Boolean,
    default: false,
  },

  // Locks the checkbox, e.g. while the message is being sent
  disabled: {
    type: Boolean,
    default: false,
  },
})

// "replace special characters" - BE strips diacritics when they are not allowed
const normalize = defineModel('normalize', { type: Boolean, default: false })

const { t } = useI18n()
const { xs } = useDisplay()
const calendarStore = useCalendarStore()

const sms = computed(() => getSmsInfo(props.text, normalize.value))
const isOverLimit = computed(() => sms.value.parts > SMS_MAX_PARTS)

// "123 z 160 zn." - over the limit the length exceeds the capacity
const charsText = computed(() => {
  if (xs.value) return sms.value.length + ' ' + t('messages_page.chars_short')

  return t('messages_page.sms_chars', { length: sms.value.length, capacity: sms.value.capacity })
})

// "2 SMS × 0,11 zł" - price of one SMS for the recipient's number (+48 and 9 digits = domestic)
const partsText = computed(() => {
  const { smsDomesticPrice, smsInternationalPrice, currency } = calendarStore
  const parts = Math.min(sms.value.parts, SMS_MAX_PARTS)
  if (!props.phone || smsDomesticPrice === '' || smsDomesticPrice == null) return `${parts} SMS`

  const price = /^\+48\d{9}$/.test(props.phone) ? smsDomesticPrice : smsInternationalPrice

  return t('messages_page.sms_parts_price', { parts, price: formatNumber(price, true, currency) })
})
</script>

<template>
  <div :class="xs ? 'd-flex flex-column align-start ga-0 flex-1-1-0' : 'sms-textarea-footer--contents'">
    <div class="d-flex align-center ga-1 flex-1-1-0">
      <VCheckbox
        v-model="normalize"
        :disabled="disabled"
        density="compact"
        hide-details
        class="flex-grow-0 ms-n1"
      >
        <template #label>
          <span class="text-no-wrap">
            {{ shortLabel || xs ? $t('messages_page.replace_diacritics_short') : $t('replaceDiacritics') }}
          </span>
        </template>
      </VCheckbox>
      <span class="d-flex cursor-pointer">
        <VIcon
          size="20"
          icon="ri-information-line"
        />
        <VMenu
          activator="parent"
          max-width="320"
          open-on-hover
        >
          <VCard>
            <VCardText class="pa-2 text-caption text-high-emphasis">
              {{ $t('diacriticsMessage') }}
            </VCardText>
          </VCard>
        </VMenu>
      </span>
    </div>
    <div
      class="d-flex align-center gc-2 text-no-wrap"
      :class="{ 'text-error': isOverLimit }"
      style="margin-left: 1px;"
    >
      <span>{{ charsText }}</span>
      <VDivider
        vertical
        length="16"
      />
      <span>{{ partsText }}</span>
    </div>
  </div>
</template>

<style scoped lang="scss">
// No box of its own, so the checkbox and the counter are the parent row's columns
.sms-textarea-footer--contents {
  display: contents;
}
</style>
